import type { AstroCookies } from "astro";
import type { Client, TokenEndpointResponse } from "oauth4webapi";
import {
  calculatePKCECodeChallenge,
  discoveryRequest,
  getValidatedIdTokenClaims,
  isOAuth2Error,
  processDiscoveryResponse,
  processRefreshTokenResponse,
  refreshTokenGrantRequest,
} from "oauth4webapi";
import { Params } from "src/pages/auth/login";

export enum AuthFlows {
  Login = "login",
  Signup = "signup",
}

export type AppState = {
  flow: AuthFlows;
  path?: string;
};

export const OIDC_COOKIES = {
  Token: "oidc_token",
  RefreshToken: "oidc_refresh_token",
  IDToken: "oidc_id_token",
  Claims: "oidc_claims",
  CodeVerifier: "oidc_code_verifier",
  State: "oidc_state",
};

export const client: Client = {
  client_id: "developer_portal",
  response_types: ["code"],
  token_endpoint_auth_method: "none", // TODO: switch to client_secret_basic
};

export const issuer: URL = new URL(import.meta.env.PUBLIC_OIDC_ISSUER);

export const authorizationServer = async () => {
  const response = await discoveryRequest(issuer);
  return await processDiscoveryResponse(issuer, response);
};

interface CookieOptions {
  expires?: Date;
  httpOnly?: boolean;
  maxAge?: number;
  path?: string;
  sameSite?: boolean | "lax" | "none" | "strict";
  secure?: boolean;
}

export const defaultCookieOptions: CookieOptions = {
  httpOnly: true,
  sameSite: "lax",
  secure: true,
  path: "/",
};

/**
 * stores the `access_token` and the `refresh_tokens` as cookies.
 */
export const persistTokens = (
  tokenSet: TokenEndpointResponse,
  cookies: AstroCookies,
) => {
  cookies.set(OIDC_COOKIES.Token, tokenSet.access_token, defaultCookieOptions);

  if (tokenSet.refresh_token) {
    cookies.set(
      OIDC_COOKIES.RefreshToken,
      tokenSet.refresh_token,
      defaultCookieOptions,
    );
  }

  if (tokenSet.id_token) {
    cookies.set(OIDC_COOKIES.IDToken, tokenSet.id_token, defaultCookieOptions);
  }

  const claims = getValidatedIdTokenClaims(tokenSet);
  if (claims) {
    cookies.set(OIDC_COOKIES.Claims, claims, {
      path: "/",
    });
  }
};

/**
 * Removes auth-related cookies from the response
 */
export function removeAuthCookies(cookies: AstroCookies): void {
  for (const key of Object.values(OIDC_COOKIES)) {
    cookies.delete(key, defaultCookieOptions);
  }
}

/**
 * Returns an `Authorization Url` for the user to authenticate with authentication provider.
 * requires a `codeVerifier` string, and a `state`.
 */
export const getAuthorizationUrl = async (
  codeVerifier: string,
  state: string,
  flow: "signup" | "login" | null = "login",
): Promise<URL> => {
  const server = await authorizationServer();
  const codeChallenge = await calculatePKCECodeChallenge(codeVerifier);

  // biome-ignore lint/style/noNonNullAssertion: FIXME
  const authorizationUrl = new URL(server.authorization_endpoint!);
  authorizationUrl.search = new URLSearchParams({
    client_id: client.client_id,
    code_challenge: codeChallenge,
    code_challenge_method: "S256",
    redirect_uri: import.meta.env.OIDC_CALLBACK,
    response_type: "code",
    scope: "openid offline classic api_keys api_keys:write email extdev",
    state: state,
    ...(flow === "signup" ? { prompt: "create" } : {}),
  }).toString();

  return authorizationUrl;
};

/**
 * Returns `End Session Url` for the user to logout.
 */
export const endSessionUrl = async (cookies: AstroCookies): Promise<string> => {
  const server = await authorizationServer();
  // biome-ignore lint/style/noNonNullAssertion: FIXME
  const authorizationUrl = new URL(server.end_session_endpoint!);
  authorizationUrl.searchParams.set(
    "post_logout_redirect_uri",
    import.meta.env.OIDC_CALLBACK.replace("/callback", "/logout/callback"),
  );

  const idToken = cookies.get(OIDC_COOKIES.IDToken);
  if (idToken) {
    authorizationUrl.searchParams.set("id_token_hint", idToken.value);
  }

  return authorizationUrl.toString();
};

/**
 * Exchanges refresh token for fresh tokenset of `access_token` and new `refresh_token`.
 */
export const exchangeRefreshToken = async (
  token: string,
): Promise<TokenEndpointResponse> => {
  const server = await authorizationServer();
  const response = await refreshTokenGrantRequest(server, client, token);

  const tokenSet = await processRefreshTokenResponse(server, client, response);
  if (isOAuth2Error(tokenSet)) {
    throw new Error(
      `Exchange refresh token ${tokenSet.error}: ${tokenSet.error_description}`,
    );
  }

  return tokenSet;
};

export const getLoginRedirectUrl = (targetUrl: URL): string => {
  const searchParams = new URLSearchParams({
    [Params.RedirectUrl]: targetUrl.pathname,
  });

  return `/auth/login?${searchParams.toString()}`;
};
